/** Public member entry. Keep this page independent of Router and Firebase for prerendering. */
import React, { useEffect } from "react";
import "./root-story.css";
import { ArrowRightIcon, LogInIcon } from "@/components/Shared/InlineIcons";
import LogoSvg from "@shared/components/LogoSvg";
import { Kicker, PhoneFrame } from "@/components/Story/StoryPieces";

type StoryLinkProps = React.PropsWithChildren<{
  to: string;
  className?: string;
  "aria-label"?: string;
}>;
const NativeStoryLink: React.FC<StoryLinkProps> = ({ to, children, ...props }) => (
  <a href={to} {...props}>{children}</a>
);

const STEPS = [
  { title: "Find your organization", body: "Search for your school, association, workplace or club. You can also use an invitation." },
  { title: "Confirm your membership", body: "Join through your organization’s verification or approval process." },
  { title: "Join the conversation", body: "Ask a question, share an update, or reconnect with other members." },
];

const RootStory: React.FC<{
  promoteSignIn?: boolean;
  LinkComponent?: React.ElementType;
}> = ({ promoteSignIn = false, LinkComponent = NativeStoryLink }) => {
  const StoryLink = LinkComponent;
  useEffect(() => { document.title = "ZuriLink · Your communities"; }, []);

  return (
    <div className="public-story bg-background text-foreground">
      <div className="sticky top-0 z-30 border-b border-border bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="container mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <StoryLink to="/" aria-label="ZuriLink" className="flex min-h-11 shrink-0 items-center">
            <LogoSvg className="h-8 w-auto sm:h-12" />
          </StoryLink>
          <nav aria-label="Account" className="flex shrink-0 items-center gap-2 text-sm">
            <StoryLink to="/enter" className="story-secondary inline-flex min-h-11 items-center rounded-md border border-border px-3 font-medium">Join</StoryLink>
            <StoryLink to="/login" className="story-button inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-md px-3 font-medium">
              <LogInIcon className="h-3.5 w-3.5" /> Sign in
            </StoryLink>
          </nav>
        </div>
      </div>
      <main>
        <header className="container mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-20">
          {promoteSignIn && (
            <p className="mb-8 rounded-md border border-border story-tint p-4 text-sm">
              Welcome back. Your session has expired.{" "}
              <StoryLink to="/login" className="inline-flex min-h-11 items-center font-medium story-accent underline">Continue to sign in</StoryLink>
            </p>
          )}
          <Kicker>Your communities</Kicker>
          <h1 className="mt-5 max-w-4xl font-display text-[2.6rem] leading-[1.1] story-heading sm:text-6xl">
            Stay connected through the communities you belong to.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed story-body sm:text-xl">
            Find people from your school, association or club. Join conversations and keep in
            touch through a community that confirms your membership.
          </p>
          <StoryLink to="/enter" className="mt-8 inline-flex min-h-11 items-center gap-2 rounded-md story-button px-5 py-3 font-medium">
            Find your community <ArrowRightIcon className="h-4 w-4" />
          </StoryLink>
          <p className="mt-4 text-sm story-body">
            Run a community?{" "}
            <StoryLink to="/founders-circle" className="inline-flex min-h-11 items-center story-accent underline">Explore the Founders Circle</StoryLink>
          </p>
        </header>

        <section aria-labelledby="join-heading" className="story-tint">
          <div className="container mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
            <h2 id="join-heading" className="font-display text-3xl story-heading">How to join</h2>
            <ol className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3">
              {STEPS.map((step, index) => (
                <li key={step.title}>
                  <span aria-hidden="true" className="font-display text-2xl story-accent">0{index + 1}</span>
                  <h3 className="mt-2 text-lg font-semibold story-heading">{step.title}</h3>
                  <p className="mt-2 leading-relaxed story-body">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="community-heading" className="container mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-12 sm:grid-cols-2 sm:px-6 sm:py-16">
          <div>
            <h2 id="community-heading" className="font-display text-3xl story-heading">Catch up with your community.</h2>
            <ul className="mt-6 list-disc space-y-4 pl-5 leading-relaxed story-body">
              <li>Follow updates and conversations in your groups.</li>
              <li>Find members with a shared affiliation.</li>
              <li>Keep in touch through direct messages.</li>
            </ul>
            <p className="mt-6 leading-relaxed story-body">The feed tells you when you’re caught up.</p>
          </div>
          <figure>
            <PhoneFrame />
            <figcaption className="mt-5 text-center text-sm story-muted">Illustrative community updates.</figcaption>
          </figure>
        </section>

        <section aria-labelledby="sharing-heading" className="story-tint">
          <div className="container mx-auto max-w-6xl px-4 py-12 sm:px-6">
            <h2 id="sharing-heading" className="font-display text-3xl story-heading">Membership is confirmed. Sharing is scoped.</h2>
            <p className="mt-4 max-w-2xl leading-relaxed story-body">
              Verified affiliations are confirmed through your organization, not just added to
              a profile. Posts have a defined audience, such as a community or relationship group.
            </p>
          </div>
        </section>

        <section className="container mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <h2 className="font-display text-3xl story-heading">Start with your organization.</h2>
          <p className="mt-4 max-w-2xl leading-relaxed story-body">Find out whether your community is on ZuriLink and how to join.</p>
          <StoryLink to="/enter" className="mt-6 inline-flex min-h-11 items-center gap-2 rounded-md story-button px-5 py-3 font-medium">
            Find your community <ArrowRightIcon className="h-4 w-4" />
          </StoryLink>
        </section>
      </main>
      <footer className="container mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 border-t border-border px-4 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-sm story-muted sm:px-6">
        <StoryLink to="/founders-circle" className="story-link inline-flex min-h-11 items-center">For organizations</StoryLink>
        <StoryLink to="/about" className="story-link inline-flex min-h-11 items-center">About</StoryLink>
        <StoryLink to="/privacy-policy" className="story-link inline-flex min-h-11 items-center">Privacy</StoryLink>
        <StoryLink to="/terms-of-service" className="story-link inline-flex min-h-11 items-center">Terms</StoryLink>
      </footer>
    </div>
  );
};
export default RootStory;
